import React from 'react'
import { BarChart2, Plus, X, Send, FileDown, AlertTriangle } from 'lucide-react'
import { AppLayout } from '../../components/layout/AppLayout'
import { Button } from '../../components/ui/Button'
import { apiFetch, openPdfInNewTab } from '../../lib/api-client'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDKK(oere: number): string {
  return (oere / 100).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr'
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })
}

function daysUntil(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null
  const d = new Date(dateStr)
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  return Math.round((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280',
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5,
}

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', height: 36, padding: '0 10px',
  border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, color: '#111827',
  background: '#fff', fontFamily: 'inherit', outline: 'none',
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface LeaseOption {
  id: string
  unit_no: string
  property_address: string
}

interface Forbrugsregnskab {
  id: string
  lease_id: string
  lease_address?: string
  unit_no?: string
  property_address?: string
  renter_name?: string
  renter_email?: string
  regnskab_year: number
  regnskab_type: string
  aconto_collected_oere: number
  actual_cost_oere: number
  tenant_share_pct: number
  tenant_actual_oere: number
  difference_oere: number
  outcome: 'refund' | 'surcharge' | 'balanced'
  outcome_invoice_id?: string | null
  regnskab_period_end: string
  delivery_deadline: string
  delivered_at?: string | null
  status: 'draft' | 'delivered' | 'disputed'
}

const TYPE_LABELS: Record<string, string> = {
  varme: 'Varme',
  vand: 'Vand',
  el: 'El',
  combined: 'Samlet forbrug',
}

// ─── Toast ────────────────────────────────────────────────────────────────────

function Toast({ message, variant, onClose }: { message: string; variant?: 'success' | 'error'; onClose: () => void }) {
  React.useEffect(() => {
    const t = setTimeout(onClose, 4000)
    return () => clearTimeout(t)
  }, [onClose])
  const bg = variant === 'error' ? '#dc2626' : '#16a34a'
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 999,
      background: bg, color: '#fff', borderRadius: 8,
      padding: '12px 20px', fontSize: 13, fontWeight: 500,
      boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
      display: 'flex', alignItems: 'center', gap: 12, maxWidth: 420,
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', padding: 0 }}>
        <X size={14} />
      </button>
    </div>
  )
}

// ─── Status chip ──────────────────────────────────────────────────────────────

function StatusChip({ status }: { status: string }) {
  const styles: Record<string, React.CSSProperties> = {
    draft: { background: '#f3f4f6', color: '#6b7280', border: '1px solid #e8eaed' },
    delivered: { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' },
    disputed: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' },
  }
  const labels: Record<string, string> = { draft: 'Kladde', delivered: 'Afleveret', disputed: 'Anfægtet' }
  const s = styles[status] ?? styles.draft
  return (
    <span style={{ ...s, borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 500 }}>
      {labels[status] ?? status}
    </span>
  )
}

// ─── Outcome chip ─────────────────────────────────────────────────────────────

function OutcomeChip({ outcome, difference_oere }: { outcome: string; difference_oere: number }) {
  if (outcome === 'refund') {
    return <span style={{ color: '#16a34a', fontWeight: 600, fontFamily: 'monospace', fontSize: 12 }}>+{formatDKK(difference_oere)}</span>
  }
  if (outcome === 'surcharge') {
    return <span style={{ color: '#dc2626', fontWeight: 600, fontFamily: 'monospace', fontSize: 12 }}>{formatDKK(Math.abs(difference_oere))}</span>
  }
  return <span style={{ color: '#9ca3af', fontSize: 12 }}>Balanceret</span>
}

// ─── Creation wizard ──────────────────────────────────────────────────────────

interface WizardProps {
  leases: LeaseOption[]
  onClose: () => void
  onCreated: (fr: Forbrugsregnskab) => void
}

function CreationWizard({ leases, onClose, onCreated }: WizardProps) {
  const now = new Date()
  const [step, setStep] = React.useState<1 | 2 | 3>(1)

  // Step 1 state
  const [leaseId, setLeaseId] = React.useState('')
  const [year, setYear] = React.useState(String(now.getFullYear() - 1))
  const [type, setType] = React.useState('varme')

  // Step 2 state
  const [actualCostDKK, setActualCostDKK] = React.useState('')
  const [tenantSharePct, setTenantSharePct] = React.useState('100')
  const [previewError, setPreviewError] = React.useState<string | null>(null)

  // Step 3 state — the created draft row
  const [draftFr, setDraftFr] = React.useState<Forbrugsregnskab | null>(null)
  const [creating, setCreating] = React.useState(false)
  const [createError, setCreateError] = React.useState<string | null>(null)
  const [delivering, setDelivering] = React.useState(false)
  const [deliverError, setDeliverError] = React.useState<string | null>(null)

  const actualCostOere = Math.round((parseFloat(actualCostDKK.replace(',', '.')) || 0) * 100)
  const sharePct = parseFloat(tenantSharePct.replace(',', '.')) || 100
  const tenantActualOere = Math.round(actualCostOere * sharePct / 100)

  // Reset preview error when inputs change
  React.useEffect(() => {
    (async () => { setPreviewError(null) })()
  }, [leaseId, year, type, actualCostDKK, tenantSharePct])

  async function createDraft() {
    setCreating(true)
    setCreateError(null)
    try {
      const fr = await apiFetch<Forbrugsregnskab>('/api/v1/forbrugsregnskab', {
        method: 'POST',
        body: JSON.stringify({
          lease_id: leaseId,
          regnskab_year: parseInt(year),
          regnskab_type: type,
          actual_cost_oere: actualCostOere,
          tenant_share_pct: sharePct,
        }),
      })
      setDraftFr(fr)
      setStep(3)
    } catch (e: unknown) {
      setCreateError(e instanceof Error ? e.message : 'Kunne ikke oprette regnskab.')
    } finally {
      setCreating(false)
    }
  }

  async function deliver() {
    if (!draftFr) return
    setDelivering(true)
    setDeliverError(null)
    try {
      await apiFetch<{ ok: boolean; outcome: string }>(`/api/v1/forbrugsregnskab/${draftFr.id}/deliver`, {
        method: 'POST',
      })
      onCreated({ ...draftFr, status: 'delivered' })
      onClose()
    } catch (e: unknown) {
      setDeliverError(e instanceof Error ? e.message : 'Kunne ikke aflevere regnskab.')
    } finally {
      setDelivering(false)
    }
  }

  const yearOptions = [String(now.getFullYear()), String(now.getFullYear() - 1), String(now.getFullYear() - 2)]

  const deadlineDays = draftFr ? daysUntil(draftFr.delivery_deadline) : null

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{
        background: '#fff', borderRadius: 10, width: '100%', maxWidth: 540,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid #e8eaed' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Nyt forbrugsregnskab</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              {step === 1 ? 'Trin 1 af 3 — Lejemål og periode' : step === 2 ? 'Trin 2 af 3 — Faktiske udgifter' : 'Trin 3 af 3 — Gennemse og aflever'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', padding: 24, flex: 1 }}>

          {/* Step 1 */}
          {step === 1 && (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Lejemål</label>
                <select style={inputStyle} value={leaseId} onChange={e => setLeaseId(e.target.value)}>
                  <option value="">— Vælg lejemål —</option>
                  {leases.map(l => (
                    <option key={l.id} value={l.id}>{l.unit_no} · {l.property_address}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Regnskabsår</label>
                  <select style={inputStyle} value={year} onChange={e => setYear(e.target.value)}>
                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Forbrugstype</label>
                  <select style={inputStyle} value={type} onChange={e => setType(e.target.value)}>
                    <option value="varme">Varme</option>
                    <option value="vand">Vand</option>
                    <option value="el">El</option>
                    <option value="combined">Samlet forbrug</option>
                  </select>
                </div>
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', background: '#f9fafb', borderRadius: 6, padding: '10px 14px', border: '1px solid #e8eaed' }}>
                Afleveringsfrist: senest 4 måneder efter regnskabsårets afslutning (30. april for kalenderårsregnskab).
              </div>
              {previewError && (
                <div style={{ background: '#fef2f2', color: '#dc2626', fontSize: 13, padding: '8px 12px', borderRadius: 6, marginTop: 16, border: '1px solid #fecaca' }}>
                  {previewError}
                </div>
              )}
            </>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Faktisk udgift (kr)</label>
                <input
                  style={inputStyle}
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="F.eks. 12500.00"
                  value={actualCostDKK}
                  onChange={e => setActualCostDKK(e.target.value)}
                />
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                  Udlejers faktiske regning for {TYPE_LABELS[type]?.toLowerCase() ?? type} i {year}
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Lejers andel (%)</label>
                <input
                  style={{ ...inputStyle, maxWidth: 140 }}
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={tenantSharePct}
                  onChange={e => setTenantSharePct(e.target.value)}
                />
              </div>

              {/* Live computed preview */}
              {actualCostOere > 0 && (
                <div style={{ background: '#f9fafb', borderRadius: 8, padding: 16, border: '1px solid #e8eaed', fontSize: 13 }}>
                  <div style={{ fontWeight: 600, color: '#6b7280', marginBottom: 10, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Beregning
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ color: '#6b7280' }}>Aconto opkrævet (beregnes ved gemning)</span>
                    <span style={{ fontFamily: 'monospace', color: '#9ca3af' }}>—</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ color: '#6b7280' }}>Lejers faktiske andel ({sharePct}%)</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#111827' }}>{formatDKK(tenantActualOere)}</span>
                  </div>
                  <div style={{ borderTop: '1px solid #e8eaed', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Difference (afhænger af aconto)</span>
                    <span style={{ color: '#9ca3af', fontFamily: 'monospace' }}>Beregnes ved gemning</span>
                  </div>
                </div>
              )}

              {createError && (
                <div style={{ background: '#fef2f2', color: '#dc2626', fontSize: 13, padding: '8px 12px', borderRadius: 6, marginTop: 16, border: '1px solid #fecaca' }}>
                  {createError}
                </div>
              )}
            </>
          )}

          {/* Step 3 */}
          {step === 3 && draftFr && (
            <>
              {/* Deadline warning */}
              {deadlineDays !== null && deadlineDays <= 30 && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '10px 14px', marginBottom: 16, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <AlertTriangle size={15} style={{ color: '#d97706', flexShrink: 0, marginTop: 1 }} />
                  <span style={{ fontSize: 13, color: '#92400e' }}>
                    {deadlineDays <= 0
                      ? 'Afleveringsfristen er overskredet! Regnskabet skal afleveres snarest.'
                      : `Afleveringsfristen udløber om ${deadlineDays} dage (${fmtDate(draftFr.delivery_deadline)}).`}
                  </span>
                </div>
              )}

              {/* Summary table */}
              <div style={{ border: '1px solid #e8eaed', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
                {[
                  ['Lejemål', draftFr.lease_address ?? `${draftFr.property_address ?? ''} ${draftFr.unit_no ?? ''}`.trim()],
                  ['Regnskabsår', String(draftFr.regnskab_year)],
                  ['Type', TYPE_LABELS[draftFr.regnskab_type] ?? draftFr.regnskab_type],
                  ['Periode', `1. jan. – 31. dec. ${draftFr.regnskab_year}`],
                  ['Afleveringsfrist', fmtDate(draftFr.delivery_deadline)],
                  ['Aconto opkrævet', formatDKK(draftFr.aconto_collected_oere)],
                  ['Faktisk udgift', formatDKK(draftFr.actual_cost_oere)],
                  ['Lejers andel', `${draftFr.tenant_share_pct}% = ${formatDKK(draftFr.tenant_actual_oere)}`],
                ].map(([k, v], i) => (
                  <div key={k} style={{
                    display: 'flex', justifyContent: 'space-between', padding: '9px 14px',
                    background: i % 2 === 0 ? '#f9fafb' : '#fff',
                    borderBottom: i < 7 ? '1px solid #e8eaed' : 'none',
                    fontSize: 13,
                  }}>
                    <span style={{ color: '#6b7280' }}>{k}</span>
                    <span style={{ color: '#111827', fontWeight: 500 }}>{v}</span>
                  </div>
                ))}

                {/* Result row */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', padding: '12px 14px',
                  background: draftFr.outcome === 'refund' ? '#f0fdf4' : draftFr.outcome === 'surcharge' ? '#fef2f2' : '#f9fafb',
                  borderTop: '2px solid #e8eaed',
                }}>
                  <span style={{ fontWeight: 700, color: '#111827', fontSize: 13 }}>
                    {draftFr.outcome === 'refund' ? 'Til godskrivning' : draftFr.outcome === 'surcharge' ? 'Efterbetaling' : 'Resultat'}
                  </span>
                  <span style={{
                    fontWeight: 700, fontFamily: 'monospace', fontSize: 13,
                    color: draftFr.outcome === 'refund' ? '#16a34a' : draftFr.outcome === 'surcharge' ? '#dc2626' : '#6b7280',
                  }}>
                    {draftFr.outcome === 'balanced'
                      ? 'Balanceret'
                      : formatDKK(Math.abs(draftFr.difference_oere))}
                  </span>
                </div>
              </div>

              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                Klik "Aflever regnskab" for at:
                {draftFr.outcome !== 'balanced' && (
                  <> oprette {draftFr.outcome === 'refund' ? 'kreditnota' : 'efterreguleringsfaktura'} og</>
                )} sende regnskabs-PDF til lejer via email.
              </div>

              {deliverError && (
                <div style={{ background: '#fef2f2', color: '#dc2626', fontSize: 13, padding: '8px 12px', borderRadius: 6, marginTop: 12, border: '1px solid #fecaca' }}>
                  {deliverError}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #e8eaed', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <div>
            {step > 1 && step < 3 && (
              <Button variant="secondary" size="sm" onClick={() => setStep(prev => (prev - 1) as 1 | 2 | 3)}>← Tilbage</Button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" size="sm" onClick={onClose}>Annuller</Button>
            {step === 1 && (
              <Button size="sm" disabled={!leaseId} onClick={() => setStep(2)}>
                Næste →
              </Button>
            )}
            {step === 2 && (
              <Button size="sm" isLoading={creating} disabled={!actualCostOere} onClick={createDraft}>
                Gem og forhåndsvis →
              </Button>
            )}
            {step === 3 && draftFr && (
              <Button size="sm" isLoading={delivering} onClick={deliver}>
                <Send size={13} />
                Aflever regnskab
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Year filter ──────────────────────────────────────────────────────────────

function getYearOptions(): number[] {
  const now = new Date()
  return [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2]
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main page
// ═══════════════════════════════════════════════════════════════════════════════

export function ForbrugsregnskabPage() {
  const [items, setItems] = React.useState<Forbrugsregnskab[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [yearFilter, setYearFilter] = React.useState<string>('')
  const [statusFilter, setStatusFilter] = React.useState<string>('')
  const [showWizard, setShowWizard] = React.useState(false)
  const [leases, setLeases] = React.useState<LeaseOption[]>([])
  const [toast, setToast] = React.useState<{ message: string; variant?: 'success' | 'error' } | null>(null)
  const [delivering, setDelivering] = React.useState<string | null>(null) // id being delivered

  const fetchItems = React.useCallback(() => {
    setLoading(true)
    const qs = yearFilter ? `?year=${yearFilter}` : ''
    apiFetch<{ items: Forbrugsregnskab[]; total: number }>(`/api/v1/forbrugsregnskab${qs}`)
      .then(r => setItems(r.items))
      .catch(() => setError('Kunne ikke hente forbrugsregnskaber.'))
      .finally(() => setLoading(false))
  }, [yearFilter])

  React.useEffect(() => { (async () => { await fetchItems() })() }, [fetchItems])
  React.useEffect(() => {
    apiFetch<LeaseOption[]>('/api/v1/leases').then(setLeases).catch(() => {})
  }, [])

  const yearOptions = getYearOptions()

  const filtered = statusFilter
    ? items.filter(fr => fr.status === statusFilter)
    : items

  // Group by year then by lease_address
  const byYear: Record<number, Forbrugsregnskab[]> = {}
  for (const fr of filtered) {
    if (!byYear[fr.regnskab_year]) byYear[fr.regnskab_year] = []
    byYear[fr.regnskab_year].push(fr)
  }
  const sortedYears = Object.keys(byYear).map(Number).sort((a, b) => b - a)

  async function downloadPdf(id: string) {
    try {
      await openPdfInNewTab(`/api/v1/forbrugsregnskab/${id}/pdf`)
    } catch {
      alert('Kunne ikke hente PDF.')
    }
  }

  async function deliverItem(fr: Forbrugsregnskab) {
    setDelivering(fr.id)
    try {
      await apiFetch(`/api/v1/forbrugsregnskab/${fr.id}/deliver`, { method: 'POST' })
      setToast({ message: `Regnskab afleveret for ${TYPE_LABELS[fr.regnskab_type] ?? fr.regnskab_type} ${fr.regnskab_year}.` })
      fetchItems()
    } catch (e: unknown) {
      setToast({ message: e instanceof Error ? e.message : 'Afleveringen mislykkedes.', variant: 'error' })
    } finally {
      setDelivering(null)
    }
  }

  return (
    <AppLayout>
      <div style={{ padding: '0 0 24px 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 3 }}>Forbrugsregnskab</div>
          <div style={{ fontSize: 14, color: '#6b7280' }}>Årlig afregning af acontobidrag for varme, vand og el</div>
        </div>
        <Button size="sm" onClick={() => setShowWizard(true)}>
          <Plus size={13} />
          Nyt regnskab
        </Button>
      </div>

      <div style={{
        background: '#ffffff', borderRadius: 10,
        border: '1px solid #e8eaed', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', borderBottom: '1px solid #e8eaed', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 5 }}>
            {[{ key: '', label: 'Alle år' }, ...yearOptions.map(y => ({ key: String(y), label: String(y) }))].map(opt => (
              <button
                key={opt.key}
                onClick={() => setYearFilter(opt.key)}
                style={{
                  padding: '4px 12px', borderRadius: 20, fontSize: 12,
                  fontWeight: yearFilter === opt.key ? 600 : 500,
                  background: yearFilter === opt.key ? '#4f46e5' : '#f7f8fa',
                  color: yearFilter === opt.key ? '#fff' : '#6b7280',
                  border: yearFilter === opt.key ? '1px solid #4f46e5' : '1px solid #e8eaed',
                  cursor: 'pointer', transition: 'all 0.12s',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 5 }}>
            {[{ key: '', label: 'Alle statusser' }, { key: 'draft', label: 'Kladde' }, { key: 'delivered', label: 'Afleveret' }].map(opt => (
              <button
                key={opt.key}
                onClick={() => setStatusFilter(opt.key)}
                style={{
                  padding: '4px 12px', borderRadius: 20, fontSize: 12,
                  fontWeight: statusFilter === opt.key ? 600 : 500,
                  background: statusFilter === opt.key ? '#111827' : '#f7f8fa',
                  color: statusFilter === opt.key ? '#fff' : '#6b7280',
                  border: statusFilter === opt.key ? '1px solid #111827' : '1px solid #e8eaed',
                  cursor: 'pointer', transition: 'all 0.12s',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {loading && <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Henter…</div>}
        {error && <div style={{ padding: 20, color: '#dc2626', fontSize: 13 }}>{error}</div>}
        {!loading && !error && filtered.length === 0 && (
          <div style={{ padding: 64, textAlign: 'center' }}>
            <BarChart2 size={32} style={{ color: '#d1d5db', marginBottom: 12 }} />
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 4 }}>Ingen forbrugsregnskaber endnu</div>
            <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 16 }}>Opret det første regnskab for et lejemål.</div>
            <Button size="sm" onClick={() => setShowWizard(true)}>
              <Plus size={13} /> Nyt regnskab
            </Button>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div>
            {sortedYears.map(yr => (
              <div key={yr}>
                {/* Year header */}
                <div style={{ padding: '10px 20px', background: '#f9fafb', borderBottom: '1px solid #e8eaed', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Regnskabsår {yr}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#fff' }}>
                      {['Lejemål', 'Lejer', 'Type', 'Aconto', 'Faktisk', 'Difference', 'Frist', 'Status', ''].map(h => (
                        <th key={h} style={{
                          padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600,
                          color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em',
                          borderBottom: '1px solid #e8eaed', whiteSpace: 'nowrap',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {byYear[yr].map((fr, i) => {
                      const days = daysUntil(fr.delivery_deadline)
                      const urgentDeadline = fr.status === 'draft' && days !== null && days <= 30
                      return (
                        <tr key={fr.id} style={{ borderBottom: i === byYear[yr].length - 1 ? 'none' : '1px solid #f3f4f6' }}>
                          <td style={{ padding: '11px 14px', fontWeight: 500, color: '#111827' }}>
                            {fr.lease_address ?? (`${fr.property_address ?? ''} ${fr.unit_no ?? ''}`.trim() || '—')}
                          </td>
                          <td style={{ padding: '11px 14px', color: '#6b7280' }}>{fr.renter_name ?? '—'}</td>
                          <td style={{ padding: '11px 14px', color: '#6b7280' }}>{TYPE_LABELS[fr.regnskab_type] ?? fr.regnskab_type}</td>
                          <td style={{ padding: '11px 14px', fontFamily: 'monospace', fontSize: 12, color: '#111827' }}>
                            {formatDKK(fr.aconto_collected_oere)}
                          </td>
                          <td style={{ padding: '11px 14px', fontFamily: 'monospace', fontSize: 12, color: '#111827' }}>
                            {formatDKK(fr.tenant_actual_oere)}
                          </td>
                          <td style={{ padding: '11px 14px' }}>
                            <OutcomeChip outcome={fr.outcome} difference_oere={fr.difference_oere} />
                          </td>
                          <td style={{ padding: '11px 14px', fontSize: 12, color: urgentDeadline ? '#d97706' : '#6b7280', whiteSpace: 'nowrap' }}>
                            {urgentDeadline && <AlertTriangle size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />}
                            {fmtDate(fr.delivery_deadline)}
                          </td>
                          <td style={{ padding: '11px 14px' }}>
                            <StatusChip status={fr.status} />
                          </td>
                          <td style={{ padding: '11px 14px', textAlign: 'right' }}>
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
                              <button
                                onClick={() => downloadPdf(fr.id)}
                                title="Hent PDF"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4, display: 'inline-flex', alignItems: 'center' }}
                              >
                                <FileDown size={14} />
                              </button>
                              {fr.status === 'draft' && (
                                <Button
                                  size="sm"
                                  isLoading={delivering === fr.id}
                                  onClick={() => deliverItem(fr)}
                                  style={{ fontSize: 11, padding: '3px 10px', height: 26 }}
                                >
                                  <Send size={11} />
                                  Aflever
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>

      {showWizard && (
        <CreationWizard
          leases={leases}
          onClose={() => setShowWizard(false)}
          onCreated={fr => {
            setToast({ message: `Regnskab afleveret for ${TYPE_LABELS[fr.regnskab_type] ?? fr.regnskab_type} ${fr.regnskab_year}.` })
            fetchItems()
          }}
        />
      )}

      {toast && <Toast message={toast.message} variant={toast.variant} onClose={() => setToast(null)} />}
    </AppLayout>
  )
}
